import { useState, useEffect, useRef } from 'react';
import { googleReviewsUrl, useGoogleReviews } from '../lib/reviews';
import GoogleLogo from './GoogleLogo';

/**
 * FloatingReviewWidget — Van Every edition
 *
 * Desktop: floating review card, bottom-left, auto-rotates live Google
 * reviews every 7s. Mobile: compact Google rating badge only.
 *
 * Renders nothing until live data arrives (no hardcoded rating/count/quote).
 * All inline styles to avoid Vortala CSS conflicts.
 */

// Avatar background colors (fallback when a review has no profile photo)
const AVATAR_COLORS = ['var(--c-pink-bright)', '#22c55e', '#a855f7', '#ec4899', 'var(--c-gold)'];

export default function FloatingReviewWidget() {
  const reviewsState = useGoogleReviews();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isFading, setIsFading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const startXRef = useRef(0);
  // Mobile badge stays out of the way until the visitor scrolls past the
  // first screen, so it never sits over a hero's Book Online button.
  const [pastHero, setPastHero] = useState(false);

  // Both the badge and the desktop card step aside for the end of the page
  // (QA 09-29: they sat on Office Hours, related links, FAQ answers and the
  // footer). They leave once the footer is within one screen of the bottom
  // edge and come back when the visitor scrolls up again.
  const [nearEnd, setNearEnd] = useState(false);

  useEffect(() => {
    const footer = document.querySelector('footer.ve-footer');
    if (!footer || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setNearEnd(entry.isIntersecting || entry.boundingClientRect.top < 0),
      { rootMargin: '0px 0px 100% 0px' },
    );
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const update = () => setPastHero(window.scrollY > window.innerHeight * 0.75);
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  // Rotate through the curated set (recent 5-star reviews with text,
  // ~30 entries) when available; fall back to the 5 Places API reviews.
  const reviews =
    reviewsState.status === 'ready'
      ? reviewsState.data.curated?.length
        ? reviewsState.data.curated
        : reviewsState.data.reviews
      : [];

  useEffect(() => {
    const dismissed = sessionStorage.getItem('reviewWidgetDismissed');
    if (dismissed) {
      setIsVisible(false);
    }
  }, []);

  const goToReview = (index: number) => {
    setIsFading(true);
    timeoutRef.current = setTimeout(() => {
      setCurrentIndex(index);
      setDisplayIndex(index);
      requestAnimationFrame(() => {
        setIsFading(false);
      });
    }, 300);
  };

  const resetAutoRotation = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (reviews.length < 2) return;
    intervalRef.current = setInterval(() => {
      setIsFading(true);
      timeoutRef.current = setTimeout(() => {
        setCurrentIndex((prev) => {
          const next = (prev + 1) % reviews.length;
          setDisplayIndex(next);
          return next;
        });
        requestAnimationFrame(() => {
          setIsFading(false);
        });
      }, 300);
    }, 7000);
  };

  useEffect(() => {
    if (!isVisible || reviews.length === 0) return;
    resetAutoRotation();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isVisible, reviews.length]);

  const handleManualNav = (index: number) => {
    goToReview(index);
    resetAutoRotation();
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    startXRef.current = e.clientX;
    setIsDragging(true);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!isDragging) return;
    setIsDragging(false);
    const diff = e.clientX - startXRef.current;
    if (Math.abs(diff) < 30) return;
    if (diff < 0) {
      handleManualNav((currentIndex + 1) % reviews.length);
    } else {
      handleManualNav((currentIndex - 1 + reviews.length) % reviews.length);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    sessionStorage.setItem('reviewWidgetDismissed', 'true');
  };

  // Render nothing until live data has arrived: no widget at all while
  // loading, on fetch failure, or if Google returns zero reviews.
  if (!isVisible || reviewsState.status !== 'ready' || reviews.length === 0) return null;

  const { data } = reviewsState;
  const showBadge = pastHero && !nearEnd;
  const review = reviews[displayIndex];
  const formattedDate = review.publishTime
    ? new Date(review.publishTime).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : review.relativeTime;

  // Generate initials for the fallback avatar (used when no profile photo)
  const initials = review.author
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const avatarBg = AVATAR_COLORS[review.author.length % AVATAR_COLORS.length];
  const reviewLink = review.googleMapsUri || review.authorUri || data.googleMapsUri || googleReviewsUrl();
  const badgeLink = data.googleMapsUri || googleReviewsUrl();

  // ---- Star SVG helper ----
  const StarIcon = ({ filled }: { filled: boolean }) => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill={filled ? 'var(--c-gold)' : 'none'}
      stroke={filled ? 'var(--c-gold)' : 'var(--c-line)'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );

  // ---- X (close) SVG helper ----
  const CloseIcon = ({ size = 14, color = 'var(--c-muted)' }: { size?: number; color?: string }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );

  return (
    <>
      {/* Desktop/mobile visibility for .review-widget-desktop and
          .review-widget-mobile lives in public/css/a11y.css (static, no
          props/state involved), so this markup does not render a <style>
          element in the body. */}

      {/* ========== DESKTOP: Full review card, bottom-left ========== */}
      <aside
        className="review-widget-desktop"
        role="complementary"
        aria-label="Customer reviews"
        style={{
          position: 'fixed',
          // Clears the round accessibility button pinned to the bottom-left
          // corner, which otherwise covers the card's footer rating line.
          bottom: '84px',
          left: '16px',
          zIndex: 1100,
          opacity: nearEnd ? 0 : 1,
          visibility: nearEnd ? 'hidden' : 'visible',
          transition: 'opacity 0.25s ease, visibility 0.25s',
          background: 'var(--c-white)',
          borderRadius: '8px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15), 0 4px 10px rgba(0,0,0,0.05)',
          border: '1px solid var(--c-line)',
          maxWidth: '280px',
        }}
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            background: 'var(--c-surface)',
            border: 'none',
            borderRadius: '50%',
            padding: '4px',
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 0,
          }}
          aria-label="Close review widget"
        >
          <CloseIcon size={14} color="var(--c-muted)" />
        </button>

        <div
          style={{
            padding: '16px',
            opacity: isFading ? 0 : 1,
            transition: 'opacity 0.3s ease-in-out',
            userSelect: 'none',
            touchAction: 'pan-y',
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
        >
          {/* Header: avatar, name, date — avatar links to the reviewer's Google profile/review */}
          <a
            href={reviewLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', textDecoration: 'none' }}
          >
            {review.authorPhoto ? (
              <img
                src={review.authorPhoto}
                alt=""
                referrerPolicy="no-referrer"
                style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0, objectFit: 'cover' }}
              />
            ) : (
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: avatarBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--c-white)',
                  fontWeight: 600,
                  fontSize: '14px',
                  fontFamily: 'var(--font-sans)',
                  flexShrink: 0,
                }}
              >
                {initials}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{
                  fontWeight: 600,
                  color: 'var(--c-ink)',
                  fontSize: '14px',
                  margin: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                {review.author}
              </p>
              {formattedDate && (
                <p style={{ fontSize: '12px', color: 'var(--c-muted)', margin: 0, fontFamily: 'var(--font-sans)' }}>
                  {formattedDate}
                </p>
              )}
            </div>
          </a>

          {/* Stars + Google link */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <div style={{ display: 'flex', gap: '2px' }}>
              {[...Array(5)].map((_, i) => (
                <StarIcon key={i} filled={i < review.rating} />
              ))}
            </div>
            <a
              href={reviewLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: '12px',
                color: 'var(--c-pink)',
                fontWeight: 500,
                textDecoration: 'none',
                fontFamily: 'var(--font-sans)',
              }}
            >
              on Google
            </a>
          </div>

          {/* Review text (unfiltered, exactly as Google returned it) */}
          <p
            aria-live="polite"
            aria-atomic="true"
            style={{
              fontSize: '14px',
              color: 'var(--c-text)',
              lineHeight: 1.6,
              margin: 0,
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontFamily: 'var(--font-sans)',
            }}
          >
            {review.text}
          </p>

          {/* Pagination: dots for short rotations, a compact counter once
              the curated rotation is too long for dots */}
          {reviews.length > 1 && reviews.length <= 10 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '12px' }}>
              {reviews.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleManualNav(index)}
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    background: index === currentIndex ? 'var(--c-pink-bright)' : 'var(--c-line)',
                    transition: 'background 0.2s',
                  }}
                  aria-label={`Go to review ${index + 1}`}
                />
              ))}
            </div>
          )}
          {/* Long curated rotations show no position indicator (Zach 08-26:
              an "n / 30" counter reads like a small review count). Reviews
              auto-rotate and stay swipeable. */}

          {/* Attribution footer with the overall rating summary */}
          <a
            href={badgeLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '5px',
              marginTop: '10px',
              paddingTop: '8px',
              borderTop: '1px solid var(--c-surface)',
              fontSize: '11px',
              color: 'var(--c-muted)',
              textDecoration: 'none',
              fontFamily: 'var(--font-sans)',
            }}
          >
            <GoogleLogo size={12} />
            {data.rating !== null && (
              <>
                <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--c-ink)' }}>
                  {data.rating.toFixed(1)}
                </span>
                <StarIcon filled />
                <span>({data.reviewCount})</span>
                <span aria-hidden="true">·</span>
              </>
            )}
            Reviews from Google
          </a>
        </div>
      </aside>

      {/* ========== MOBILE: Compact Google rating badge ========== */}
      {/* One row with the accessibility button (left, 44px at 13-18px from
          the corner) and the chat bubble (right, 56px at 16px), instead of a
          second row stacked above them: the floating buttons then own a
          single ~72px band, which the footer reserves at the page end
          (chrome.css --ve-float-reserve). chrome.css lifts the whole row
          above the phone booking bar while it is up. */}
      <a
        className="review-widget-mobile"
        href={badgeLink}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={data.rating ? `${data.rating.toFixed(1)} stars on Google, ${data.reviewCount} reviews` : 'See our reviews on Google'}
        style={{
          position: 'fixed',
          bottom: '14px',
          left: '74px',
          zIndex: 1100,
          background: 'var(--c-white)',
          borderRadius: '999px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          border: '1px solid var(--c-line)',
          padding: '8px 12px',
          // display comes from the .review-widget-mobile CSS rules; an inline
          // display:flex here would defeat the desktop display:none and float
          // this badge over the desktop review card.
          alignItems: 'center',
          gap: '8px',
          textDecoration: 'none',
          // Hidden (and out of the tab order) until past the first screen,
          // and again near the end of the page.
          opacity: showBadge ? 1 : 0,
          visibility: showBadge ? 'visible' : 'hidden',
          transform: showBadge ? 'none' : 'translateY(8px)',
          transition: 'opacity 0.25s ease, transform 0.25s ease, visibility 0.25s',
        }}
      >
        <GoogleLogo />
        {data.rating !== null && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--c-ink)', fontFamily: 'var(--font-sans)' }}>
              {data.rating.toFixed(1)}
            </span>
            <StarIcon filled />
            <span style={{ fontSize: '12px', color: 'var(--c-muted)', fontFamily: 'var(--font-sans)' }}>
              ({data.reviewCount})
            </span>
          </div>
        )}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDismiss();
          }}
          style={{
            marginLeft: '4px',
            background: 'none',
            border: 'none',
            padding: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 0,
            minWidth: '44px',
            minHeight: '44px',
          }}
          aria-label="Dismiss review badge"
        >
          <CloseIcon size={14} color="var(--c-muted)" />
        </button>
      </a>
    </>
  );
}
